/**
 * Interactive 3D Hero Section
 * Premium interactive features for Shopify themes
 */

class Interactive3DSection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.modelViewer = section.querySelector('model-viewer');
    this.loadingContainer = section.querySelector('.loading-progress-container');
    this.loadingBar = section.querySelector('.loading-bar');
    this.progressFill = section.querySelector('.progress-fill');
    this.progressGlow = section.querySelector('.progress-glow');
    this.mouseFollower = section.querySelector('.mouse-follower');
    this.modelContainer = section.querySelector('.model-container');

    // Get settings from data attributes
    this.mouseReactive = this.loadingBar?.dataset.mouseReactive === 'true';
    this.enableParallax = this.modelContainer?.dataset.mouseParallax === 'true';
    this.parallaxIntensity = parseFloat(this.modelContainer?.dataset.parallaxIntensity) || 20;

    // State
    this.isModelLoaded = false;
    this.currentProgress = 0;
    this.targetProgress = 0;

    this.init();
  }

  init() {
    if (this.modelViewer) {
      this.setupModelLoading();
      this.setupMouseReactivity();
      this.setupMobileInteractions();
      this.setupParallax();
      this.initParticles();
      this.setupAdvancedLighting();

      // Animate progress smoothly
      this.animateProgress();
    }
  }

  /**
   * Setup mouse reactivity for loading bar and parallax
   */
  setupMouseReactivity() {
    if (!this.mouseReactive && !this.enableParallax && !this.mouseFollower) return;

    // Use throttled mousemove for better performance
    let ticking = false;

    document.addEventListener('mousemove', (e) => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleMouseMove(e);
          ticking = false;
        });
        ticking = true;
      }
    });

    // Reset on mouse leave
    this.section.addEventListener('mouseleave', () => {
      if (this.modelViewer && this.enableParallax) {
        this.modelViewer.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg)';
      }
    });
  }

  /**
   * Handle mouse movement for all interactive features
   */
  handleMouseMove(e) {
    const rect = this.section.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    // Update loading bar based on mouse position
    if (this.mouseReactive && this.progressFill && !this.isModelLoaded) {
      const progress = Math.min(Math.max(x * 100, 0), 100);
      this.targetProgress = progress;
    }

    // 3D tilt effect (parallax)
    if (this.enableParallax && this.modelViewer) {
      const tiltX = (y - 0.5) * this.parallaxIntensity;
      const tiltY = (x - 0.5) * -this.parallaxIntensity;

      this.modelViewer.style.transform = `
        perspective(1000px)
        rotateX(${tiltX}deg)
        rotateY(${tiltY}deg)
      `;
    }

    // Mouse follower
    if (this.mouseFollower) {
      this.mouseFollower.style.transform = `
        translate(${e.clientX - 10}px, ${e.clientY - 10}px)
      `;
    }
  }

  /**
   * Setup mobile interactions (touch and device orientation)
   */
  setupMobileInteractions() {
    // Device orientation for mobile parallax
    if (window.DeviceOrientationEvent && 'ontouchstart' in window) {
      // Request permission for iOS 13+
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        // Don't request automatically, wait for user interaction
        this.section.addEventListener('touchstart', () => {
          this.requestOrientationPermission();
        }, { once: true });
      } else {
        this.setupDeviceOrientation();
      }
    }

    // Touch interactions for loading bar
    if (this.loadingBar) {
      let touchStartX = null;

      this.section.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
      }, { passive: true });

      this.section.addEventListener('touchmove', (e) => {
        if (!touchStartX || this.isModelLoaded) return;

        const touchCurrentX = e.touches[0].clientX;
        const rect = this.section.getBoundingClientRect();
        const progress = ((touchCurrentX - rect.left) / rect.width) * 100;

        this.targetProgress = Math.min(Math.max(progress, 0), 100);
      }, { passive: true });

      this.section.addEventListener('touchend', () => {
        touchStartX = null;
      });
    }
  }

  /**
   * Request device orientation permission for iOS 13+
   */
  async requestOrientationPermission() {
    try {
      const permission = await DeviceOrientationEvent.requestPermission();
      if (permission === 'granted') {
        this.setupDeviceOrientation();
      }
    } catch (error) {
      console.log('Device orientation not available:', error);
    }
  }

  /**
   * Setup device orientation for mobile
   */
  setupDeviceOrientation() {
    window.addEventListener('deviceorientation', (e) => {
      if (!this.modelViewer || !this.enableParallax) return;

      // Use beta (front-back tilt) and gamma (left-right tilt)
      const tiltX = (e.beta || 0) / 3; // Range: -180 to 180
      const tiltY = (e.gamma || 0) / 3; // Range: -90 to 90

      // Constrain values
      const constrainedTiltX = Math.min(Math.max(tiltX, -this.parallaxIntensity), this.parallaxIntensity);
      const constrainedTiltY = Math.min(Math.max(tiltY, -this.parallaxIntensity), this.parallaxIntensity);

      this.modelViewer.style.transform = `
        perspective(1000px)
        rotateX(${constrainedTiltX}deg)
        rotateY(${constrainedTiltY}deg)
      `;

      // Update progress based on tilt (optional)
      if (this.mouseReactive && !this.isModelLoaded && this.progressFill) {
        const progress = ((e.gamma + 90) / 180) * 100;
        this.targetProgress = Math.min(Math.max(progress, 0), 100);
      }
    });
  }

  /**
   * Setup model loading progress
   */
  setupModelLoading() {
    // Set timeout fallback to hide loading screen after 30 seconds
    const loadingTimeout = setTimeout(() => {
      if (!this.isModelLoaded && this.loadingContainer) {
        console.warn('Model loading timeout - hiding loading screen');
        this.hideLoadingScreen();
      }
    }, 30000);

    // Handle model load event
    this.modelViewer.addEventListener('load', () => {
      clearTimeout(loadingTimeout);
      this.isModelLoaded = true;
      this.targetProgress = 100;

      setTimeout(() => {
        this.hideLoadingScreen();
        this.section.classList.add('model-loaded');
      }, 500);
    });

    // Handle model progress
    this.modelViewer.addEventListener('progress', (e) => {
      const progress = e.detail.totalProgress * 100;
      this.targetProgress = Math.max(this.targetProgress, progress);
    });

    // Handle model errors
    this.modelViewer.addEventListener('error', (e) => {
      clearTimeout(loadingTimeout);
      console.error('Model loading error:', e);
      if (this.loadingContainer) {
        const loadingText = this.loadingContainer.querySelector('.loading-text');
        if (loadingText) {
          loadingText.textContent = 'Error loading model';
          loadingText.style.color = '#dd1d1d';
        }
      }
      // Hide loading screen after error
      setTimeout(() => {
        this.hideLoadingScreen();
      }, 2000);
    });

    // Handle poster dismissed event (when reveal happens)
    this.modelViewer.addEventListener('poster-dismissed', () => {
      console.log('Model poster dismissed - model is now visible');
    });
  }

  /**
   * Hide the loading screen
   */
  hideLoadingScreen() {
    if (this.loadingContainer) {
      this.loadingContainer.dataset.loadingState = 'loaded';
      this.loadingContainer.style.opacity = '0';
      setTimeout(() => {
        this.loadingContainer.style.display = 'none';
      }, 600);
    }
  }

  /**
   * Animate progress bar smoothly
   */
  animateProgress() {
    const animate = () => {
      // Smooth interpolation
      this.currentProgress += (this.targetProgress - this.currentProgress) * 0.1;

      if (this.progressFill) {
        this.progressFill.style.width = `${this.currentProgress}%`;
      }

      // Move glow effect
      if (this.progressGlow) {
        this.progressGlow.style.right = `${100 - this.currentProgress}%`;
      }

      // Continue animation
      requestAnimationFrame(animate);
    };

    animate();
  }

  /**
   * Initialize particle system
   */
  initParticles() {
    const canvases = this.section.querySelectorAll('.particle-canvas');

    canvases.forEach(canvas => {
      this.setupParticleCanvas(canvas);
    });
  }

  /**
   * Setup individual particle canvas
   */
  setupParticleCanvas(canvas) {
    const ctx = canvas.getContext('2d');

    // Set canvas size to match container
    const resizeCanvas = () => {
      const rect = this.section.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Get settings
    const particleCount = parseInt(canvas.dataset.particleCount) || 50;
    const particleColor = canvas.dataset.particleColor || '#ffffff';
    const particleSize = parseFloat(canvas.dataset.particleSize) || 2;
    const particleSpeed = parseFloat(canvas.dataset.particleSpeed) || 1;

    // Create particles
    const particles = [];

    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() * 2 - 1) * particleSpeed;
        this.vy = (Math.random() * 2 - 1) * particleSpeed;
        this.size = Math.random() * particleSize + 1;
        this.opacity = Math.random() * 0.5 + 0.2;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Wrap around edges
        if (this.x < 0) this.x = canvas.width;
        if (this.x > canvas.width) this.x = 0;
        if (this.y < 0) this.y = canvas.height;
        if (this.y > canvas.height) this.y = 0;
      }

      draw() {
        ctx.fillStyle = this.hexToRgba(particleColor, this.opacity);
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }

      hexToRgba(hex, alpha) {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
    }

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      // Draw connections between nearby particles
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.strokeStyle = p1.hexToRgba(particleColor, 0.1 * (1 - distance / 100));
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    };

    animate();
  }

  /**
   * Setup scroll parallax for floating elements
   */
  setupParallax() {
    const parallaxElements = this.section.querySelectorAll('[data-parallax-speed]');

    if (parallaxElements.length === 0) return;

    let ticking = false;

    const updateParallax = () => {
      const scrolled = window.pageYOffset;
      const sectionTop = this.section.offsetTop;
      const sectionHeight = this.section.offsetHeight;
      const viewportHeight = window.innerHeight;

      // Only apply parallax when section is in viewport
      if (scrolled + viewportHeight > sectionTop && scrolled < sectionTop + sectionHeight) {
        parallaxElements.forEach(element => {
          const speed = parseFloat(element.dataset.parallaxSpeed) || 0.5;
          const yPos = -(scrolled - sectionTop) * speed;
          element.style.transform = `translateY(${yPos}px)`;
        });
      }
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateParallax();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Initial call
    updateParallax();
  }

  /**
   * Setup advanced lighting controls
   */
  setupAdvancedLighting() {
    if (!this.modelViewer || !this.modelContainer) return;

    // Wait for model to load before applying lighting
    this.modelViewer.addEventListener('load', () => {
      requestAnimationFrame(() => {
        this.applyAdvancedLighting();
      });
    });
  }

  /**
   * Apply advanced lighting to Three.js scene
   */
  applyAdvancedLighting() {
    try {
      // Get lighting settings from data attributes
      const ambientColor = this.modelContainer.dataset.ambientColor || '#ffffff';
      const ambientIntensity = parseFloat(this.modelContainer.dataset.ambientIntensity) || 1;
      const dirColor = this.modelContainer.dataset.dirColor || '#ffffff';
      const dirIntensity = parseFloat(this.modelContainer.dataset.dirIntensity) || 0.8;
      const dirX = parseFloat(this.modelContainer.dataset.dirX) || 0;
      const dirY = parseFloat(this.modelContainer.dataset.dirY) || 5;
      const dirZ = parseFloat(this.modelContainer.dataset.dirZ) || 5;
      const enableShadows = this.modelContainer.dataset.enableShadows === 'true';
      const shadowSoftness = parseFloat(this.modelContainer.dataset.shadowSoftness) || 0.5;
      const envIntensity = parseFloat(this.modelContainer.dataset.envIntensity) || 1;
      const exposure = parseFloat(this.modelContainer.dataset.exposure) || 1;

      // Access the Three.js scene from model-viewer
      const scene = this.modelViewer.model;
      if (!scene) {
        console.warn('Scene not ready for lighting setup');
        return;
      }

      // Set exposure (tone mapping)
      if (this.modelViewer.renderer) {
        this.modelViewer.renderer.toneMappingExposure = exposure;
      }

      // Traverse the scene to find and update lights
      scene.traverse((child) => {
        // Update ambient lights
        if (child.isAmbientLight) {
          child.color.setStyle(ambientColor);
          child.intensity = ambientIntensity;
        }

        // Update directional lights
        if (child.isDirectionalLight) {
          child.color.setStyle(dirColor);
          child.intensity = dirIntensity;
          child.position.set(dirX, dirY, dirZ);
          child.castShadow = enableShadows;

          if (enableShadows && child.shadow) {
            // Adjust shadow properties for softness
            const mapSize = Math.floor(512 + (shadowSoftness * 1536)); // 512 to 2048
            child.shadow.mapSize.width = mapSize;
            child.shadow.mapSize.height = mapSize;
            child.shadow.radius = shadowSoftness * 3;
            child.shadow.camera.near = 0.5;
            child.shadow.camera.far = 50;
          }
        }

        // Update materials to use environment map
        if (child.isMesh && child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              if (mat.envMapIntensity !== undefined) {
                mat.envMapIntensity = envIntensity;
              }
            });
          } else {
            if (child.material.envMapIntensity !== undefined) {
              child.material.envMapIntensity = envIntensity;
            }
          }
        }
      });

      // Force render update
      this.modelViewer.jumpCameraToGoal();

      console.log('Advanced lighting applied successfully');
    } catch (error) {
      console.warn('Advanced lighting setup error:', error);
    }
  }
}

/**
 * Initialize all 3D interactive sections on the page
 */
function initInteractive3DSections() {
  const sections = document.querySelectorAll('.hero-3d-interactive');
  sections.forEach(section => {
    new Interactive3DSection(section);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInteractive3DSections);
} else {
  initInteractive3DSections();
}

// Reinitialize on Shopify theme editor section load
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.contains('hero-3d-interactive')) {
      new Interactive3DSection(event.target);
    }
  });
}
